import type { Payments_CardBrand as CardBrandEnum } from '__generated__/graphql-types';
import { Payments_WalletType as WalletType } from '__generated__/graphql-types';

import {
  STRIPE_CONSUMER_PAYMENT_PROCESSOR_IDS,
  acceptedCreditCards,
  acceptedCreditCardsStripe,
} from 'bundles/payments-common/common/constants';

import APPLE_PAY_ICON from 'images/payments/apple_pay.svg';
import GOOGLE_PAY_ICON from 'images/payments/google_pay.svg';
import LINK_PAY_ICON from 'images/payments/link_pay.svg';
import CARD_ICON from 'images/signature/cc/credit.png';

/**
 * Gets the icon URL for a wallet type (Apple Pay, Google Pay, Link, etc.)
 * @param walletType - The wallet type enum value
 * @returns The icon URL for the wallet type, or null if not a recognized wallet type
 */
export const getWalletTypeIcon = (walletType?: WalletType | string | null): string | null => {
  if (!walletType) return null;

  switch (walletType) {
    case WalletType.GooglePay:
      return GOOGLE_PAY_ICON;
    case WalletType.ApplePay:
      return APPLE_PAY_ICON;
    case WalletType.Link:
      return LINK_PAY_ICON;
    default:
      return null;
  }
};

/**
 * Gets the card brand icon URL based on card brand and payment processor
 * @param cardBrand - The card brand (can be string name or enum)
 * @param paymentProcessorId - Optional payment processor ID to determine which card array to use
 * @returns The icon URL for the card brand, defaults to generic card icon if not found
 */
export const getCardBrandIcon = (
  cardBrand?: string | CardBrandEnum | null,
  paymentProcessorId?: string | null
): string => {
  if (!cardBrand) return CARD_ICON;

  const isStripeProcessor =
    paymentProcessorId && STRIPE_CONSUMER_PAYMENT_PROCESSOR_IDS.includes(paymentProcessorId || '');

  if (isStripeProcessor) {
    const card = acceptedCreditCardsStripe.find((c) => c.enumName === cardBrand);
    return card ? card.url : CARD_ICON;
  }

  const card = acceptedCreditCards.find((c) => c.name === cardBrand);
  return card ? card.url : CARD_ICON;
};
